import axios from "axios";
import {
  getHubCpId,
  getRbacAuthToken,
  getRbacCognitoStatusUrl,
  getRbacEmailVerificationUrl,
  getRbacEmailVerifyCodeUrl,
  getRbacLocationByHubCpUrl,
  getRbacMobileVerificationUrl,
  getRbacMobileVerifyCodeUrl,
  getRbacRequestHeaders,
  getRbacUsersListUrl,
  getRbacUsersUrl,
  logRbacFormDataPayload,
} from "./rbacApi";
import { stripStaffMobileNumber } from "./rbacUserPayload";

const RBAC_DEFAULT_ROLE_NAME = "HUB-OPERATOR";

export const fetchRbacLocationByHubCpId = async (hubCpId) => {
  const resolvedHubCpId = hubCpId || getHubCpId();
  const url = getRbacLocationByHubCpUrl(resolvedHubCpId);
  const token = getRbacAuthToken();

  if (!resolvedHubCpId) {
    return {
      locationId: null,
      location: null,
      locationName: "",
      error: "Hub id is missing. Please select an active hub.",
    };
  }

  if (!url) {
    return {
      locationId: null,
      location: null,
      locationName: "",
      error: "RBAC locations API configuration is missing.",
    };
  }

  if (!token) {
    return {
      locationId: null,
      location: null,
      locationName: "",
      error: "Authorization token is missing. Unable to fetch location.",
    };
  }

  try {
    console.log("[RBAC Staff] Location request", { url, hubCpId: resolvedHubCpId });

    const response = await axios.get(url, {
      headers: {
        accept: "application/json",
        authorization: `Bearer ${token}`,
      },
    });

    const location = response?.data?.data || response?.data;
    const locationId = location?.id;
    const locationName = location?.name || location?.locationName || "";

    console.log("[RBAC Staff] Location response", response?.data);

    if (!locationId) {
      return {
        locationId: null,
        location: null,
        locationName: "",
        error: "Location not found for the selected hub.",
      };
    }

    return { locationId, location, locationName, error: null };
  } catch (error) {
    console.log("[RBAC Staff] Location error response", error?.response?.data || error);

    return {
      locationId: null,
      location: null,
      locationName: "",
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch RBAC location",
    };
  }
};

const resolveRbacLocationName = async (locationName) => {
  if (locationName) {
    return locationName;
  }

  const { location, locationName: fetchedLocationName } =
    await fetchRbacLocationByHubCpId();

  return fetchedLocationName || location?.name || location?.locationName || "";
};

export const fetchRbacUsers = async ({
  page = 1,
  limit = 20,
  tenantName,
  confirmationStatus,
  name,
  email,
  mobileNumber,
  roleName,
  locationName,
  applyDefaultFilters = true,
} = {}) => {
  const url = getRbacUsersListUrl();
  const token = getRbacAuthToken();
  const isSearchRequest = Boolean(
    name ||
      email ||
      mobileNumber ||
      confirmationStatus === true ||
      confirmationStatus === false
  );
  const shouldApplyDefaultFilters = applyDefaultFilters && !isSearchRequest;
  const resolvedRoleName = shouldApplyDefaultFilters
    ? roleName || RBAC_DEFAULT_ROLE_NAME
    : roleName;
  const resolvedLocationName = shouldApplyDefaultFilters
    ? await resolveRbacLocationName(locationName)
    : locationName || "";

  if (!url) {
    return {
      users: [],
      pagination: { page: 1, limit, total: 0 },
      error: "RBAC users API configuration is missing.",
    };
  }

  if (!token) {
    return {
      users: [],
      pagination: { page: 1, limit, total: 0 },
      error: "Authorization token is missing. Unable to load users.",
    };
  }

  try {
    const requestParams = {
      page,
      limit,
      ...(tenantName ? { tenantName } : {}),
      ...(confirmationStatus === true || confirmationStatus === false
        ? { confirmationStatus }
        : {}),
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      ...(mobileNumber ? { mobileNumber } : {}),
    };

    if (resolvedRoleName) {
      requestParams.roleName = resolvedRoleName;
    }

    if (resolvedLocationName) {
      requestParams.locationName = resolvedLocationName;
    }

    console.log("[RBAC Staff] Fetch users request", {
      url,
      isSearchRequest,
      ...requestParams,
    });

    const response = await axios.get(url, {
      headers: getRbacRequestHeaders(),
      params: requestParams,
    });

    console.log("[RBAC Staff] Fetch users response", response?.data);

    const responseData = response?.data || {};

    if (responseData?.success === false) {
      return {
        users: [],
        pagination: { page, limit, total: 0 },
        error: responseData?.message || "Failed to load users",
      };
    }

    return {
      users: responseData?.data || [],
      pagination: responseData?.pagination || {
        page,
        limit,
        total: responseData?.data?.length || 0,
      },
      error: null,
    };
  } catch (error) {
    return {
      users: [],
      pagination: { page, limit, total: 0 },
      error: error?.response?.data?.message || "Failed to load users",
    };
  }
};

export const fetchRbacUserByEmail = async (email) => {
  const trimmedEmail = String(email || "").trim();

  if (!trimmedEmail) {
    return { user: null, error: "Email is required." };
  }

  const { users, error } = await fetchRbacUsers({
    page: 1,
    limit: 10,
    email: trimmedEmail,
  });

  if (error) {
    return { user: null, error };
  }

  if (!users.length) {
    return { user: null, error: "User not found." };
  }

  const user = users[0];
  const locations = (user?.tenantAccess || []).flatMap((tenant) =>
    (tenant?.assignedLocations || [])
      .map((location) => location?.locationId)
      .filter(Boolean)
      .map((locationId) => ({ locationId }))
  );

  return {
    user: {
      ...user,
      userId: user?.userId || user?.id,
      locations: user?.locations?.length ? user.locations : locations,
    },
    error: null,
  };
};

export const fetchRbacUserById = async (userId) => {
  if (!userId) {
    return { user: null, error: "User id is required." };
  }

  const baseUrl = getRbacUsersUrl();
  const token = getRbacAuthToken();

  if (!baseUrl) {
    return {
      user: null,
      error: "RBAC users API configuration is missing.",
    };
  }

  if (!token) {
    return {
      user: null,
      error: "Authorization token is missing. Unable to load user.",
    };
  }

  try {
    const response = await axios.get(`${baseUrl}/${userId}`, {
      headers: getRbacRequestHeaders(),
    });

    const payload = response?.data?.data || response?.data;
    const user =
      payload?.user && typeof payload.user === "object" && !Array.isArray(payload.user)
        ? payload.user
        : payload;

    if (!user || typeof user !== "object" || Array.isArray(user)) {
      return { user: null, error: "User not found." };
    }

    return { user, error: null };
  } catch (directError) {
    const { users, error } = await fetchRbacUsers({
      page: 1,
      limit: 10,
    });

    if (error) {
      return {
        user: null,
        error:
          directError?.response?.data?.message ||
          directError?.response?.data?.error ||
          error,
      };
    }

    if (!users.length) {
      return { user: null, error: "User not found." };
    }

    return { user: users[0], error: null };
  }
};

export const updateRbacUser = async (userId, formData) => {
  const baseUrl = getRbacUsersUrl();
  const token = getRbacAuthToken();

  if (!baseUrl) {
    return {
      data: null,
      error: "RBAC users API configuration is missing.",
    };
  }

  if (!token) {
    return {
      data: null,
      error: "Authorization token is missing. Unable to update user.",
    };
  }

  if (!userId) {
    return {
      data: null,
      error: "User id is required.",
    };
  }

  try {
    console.log("[RBAC Staff] Update user request", {
      url: `${baseUrl}/${userId}`,
      userId,
    });
    logRbacFormDataPayload("Update user payload", formData);

    const response = await axios.patch(`${baseUrl}/${userId}`, formData, {
      headers: getRbacRequestHeaders(),
    });

    console.log("[RBAC Staff] Update user response", response?.data);

    const responseData = response?.data || {};

    return {
      data: responseData?.data || responseData,
      message: responseData?.message,
      success: responseData?.success,
      error: null,
    };
  } catch (error) {
    console.log("[RBAC Staff] Update user error response", error?.response?.data || error);

    return {
      data: null,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update staff",
    };
  }
};

export const sendRbacMobileVerificationCode = async (mobileNumber, userId) => {
  const trimmedMobile = stripStaffMobileNumber(mobileNumber);
  const resolvedUserId = userId ? String(userId).trim() : "";
  const url = getRbacMobileVerificationUrl();
  const token = getRbacAuthToken();

  if (!trimmedMobile) {
    return {
      data: null,
      error: "Mobile number is required.",
    };
  }

  if (!url) {
    return {
      data: null,
      error: "RBAC mobile verification API configuration is missing.",
    };
  }

  if (!token) {
    return {
      data: null,
      error: "Authorization token is missing. Unable to send verification code.",
    };
  }

  try {
    const payload = {
      mobileNumber: trimmedMobile,
      ...(resolvedUserId ? { userId: resolvedUserId } : {}),
    };

    console.log("[RBAC Staff] Send mobile verification request", {
      url,
      ...payload,
    });

    const response = await axios.post(
      url,
      payload,
      {
        headers: getRbacRequestHeaders({
          "content-type": "application/json",
        }),
      }
    );

    console.log("[RBAC Staff] Send mobile verification response", response?.data);

    const responseData = response?.data || {};
    const statusCode = Number(
      responseData?.status_code ?? responseData?.statusCode ?? response?.status
    );

    if (responseData?.success === false || statusCode >= 400) {
      return {
        data: null,
        error: responseData?.message || "Failed to send verification code",
      };
    }

    return {
      data: responseData?.data || responseData,
      message: responseData?.message,
      success: responseData?.success,
      error: null,
    };
  } catch (error) {
    console.log(
      "[RBAC Staff] Send mobile verification error response",
      error?.response?.data || error
    );

    return {
      data: null,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to send verification code",
    };
  }
};

export const verifyRbacMobileVerificationCode = async (mobileNumber, code, userId) => {
  const trimmedMobile = stripStaffMobileNumber(mobileNumber);
  const trimmedCode = String(code ?? "").trim();
  const resolvedUserId = userId ? String(userId).trim() : "";
  const url = getRbacMobileVerifyCodeUrl();
  const token = getRbacAuthToken();

  if (!trimmedMobile) {
    return {
      verified: false,
      error: "Mobile number is required.",
    };
  }

  if (!trimmedCode) {
    return {
      verified: false,
      error: "Verification code is required.",
    };
  }

  if (!url) {
    return {
      verified: false,
      error: "RBAC mobile verification API configuration is missing.",
    };
  }

  if (!token) {
    return {
      verified: false,
      error: "Authorization token is missing. Unable to verify code.",
    };
  }

  try {
    const payload = {
      mobileNumber: trimmedMobile,
      code: trimmedCode,
      ...(resolvedUserId ? { userId: resolvedUserId } : {}),
    };

    console.log("[RBAC Staff] Verify mobile code request", {
      url,
      ...payload,
    });

    const response = await axios.post(url, payload, {
      headers: getRbacRequestHeaders({
        "content-type": "application/json",
      }),
    });

    console.log("[RBAC Staff] Verify mobile code response", response?.data);

    const responseData = response?.data || {};

    if (responseData?.success === false) {
      return {
        verified: false,
        error: responseData?.message || "Wrong OTP",
      };
    }

    const verified =
      responseData?.success === true ||
      responseData?.data === true ||
      responseData?.verified === true;

    return {
      verified,
      message: responseData?.message,
      error: verified ? null : responseData?.message || "Wrong OTP",
    };
  } catch (error) {
    console.log(
      "[RBAC Staff] Verify mobile code error response",
      error?.response?.data || error
    );

    return {
      verified: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Wrong OTP",
    };
  }
};

export const sendRbacEmailVerificationCode = async (newEmail, userId) => {
  const trimmedEmail = String(newEmail || "").trim();
  const resolvedUserId = userId ? String(userId).trim() : "";
  const url = getRbacEmailVerificationUrl();
  const token = getRbacAuthToken();

  if (!trimmedEmail) {
    return {
      data: null,
      error: "Email is required.",
    };
  }

  if (!url) {
    return {
      data: null,
      error: "RBAC email verification API configuration is missing.",
    };
  }

  if (!token) {
    return {
      data: null,
      error: "Authorization token is missing. Unable to send verification code.",
    };
  }

  const payload = {
    newEmail: trimmedEmail,
    ...(resolvedUserId ? { userId: resolvedUserId } : {}),
  };

  try {
    console.log("[RBAC Staff] Send email verification request", {
      url,
      ...payload,
    });

    const response = await axios.post(url, payload, {
      headers: getRbacRequestHeaders({
        "content-type": "application/json",
      }),
    });

    console.log("[RBAC Staff] Send email verification response", response?.data);

    const responseData = response?.data || {};
    const statusCode = Number(
      responseData?.status_code ?? responseData?.statusCode ?? response?.status
    );

    if (responseData?.success === false || statusCode >= 400) {
      return {
        data: null,
        error: responseData?.message || "Failed to send verification code",
      };
    }

    return {
      data: responseData?.data || responseData,
      message: responseData?.message,
      success: responseData?.success,
      error: null,
    };
  } catch (error) {
    console.log(
      "[RBAC Staff] Send email verification error response",
      error?.response?.data || error
    );

    return {
      data: null,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to send verification code",
    };
  }
};

export const verifyRbacEmailVerificationCode = async (newEmail, code, userId) => {
  const trimmedEmail = String(newEmail || "").trim();
  const trimmedCode = String(code ?? "").trim();
  const resolvedUserId = userId ? String(userId).trim() : "";
  const url = getRbacEmailVerifyCodeUrl();
  const token = getRbacAuthToken();

  if (!trimmedEmail) {
    return {
      verified: false,
      error: "Email is required.",
    };
  }

  if (!trimmedCode) {
    return {
      verified: false,
      error: "Verification code is required.",
    };
  }

  if (!url) {
    return {
      verified: false,
      error: "RBAC email verification API configuration is missing.",
    };
  }

  if (!token) {
    return {
      verified: false,
      error: "Authorization token is missing. Unable to verify code.",
    };
  }

  try {
    const payload = {
      newEmail: trimmedEmail,
      code: trimmedCode,
      ...(resolvedUserId ? { userId: resolvedUserId } : {}),
    };

    console.log("[RBAC Staff] Verify email code request", {
      url,
      ...payload,
    });

    const response = await axios.post(url, payload, {
      headers: getRbacRequestHeaders({
        "content-type": "application/json",
      }),
    });

    console.log("[RBAC Staff] Verify email code response", response?.data);

    const responseData = response?.data || {};

    if (responseData?.success === false) {
      return {
        verified: false,
        error: responseData?.message || "Wrong OTP",
      };
    }

    const verified =
      responseData?.success === true ||
      responseData?.data === true ||
      responseData?.verified === true;

    return {
      verified,
      message: responseData?.message,
      error: verified ? null : responseData?.message || "Wrong OTP",
    };
  } catch (error) {
    console.log(
      "[RBAC Staff] Verify email code error response",
      error?.response?.data || error
    );

    return {
      verified: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Wrong OTP",
    };
  }
};

export const updateRbacUserCognitoStatus = async (email, enabled) => {
  const trimmedEmail = String(email || "").trim();
  const url = getRbacCognitoStatusUrl(trimmedEmail);
  const token = getRbacAuthToken();

  if (!trimmedEmail) {
    return {
      data: null,
      error: "Email is required.",
    };
  }

  if (!url) {
    return {
      data: null,
      error: "RBAC cognito status API configuration is missing.",
    };
  }

  if (!token) {
    return {
      data: null,
      error: "Authorization token is missing. Unable to update user status.",
    };
  }

  try {
    console.log("[RBAC Staff] Cognito status request", {
      url,
      email: trimmedEmail,
      enabled,
    });

    const response = await axios.patch(
      url,
      { enabled: Boolean(enabled) },
      {
        headers: getRbacRequestHeaders({
          "content-type": "application/json",
        }),
      }
    );

    console.log("[RBAC Staff] Cognito status response", response?.data);

    const responseData = response?.data || {};

    if (responseData?.success === false) {
      return {
        data: null,
        error: responseData?.message || "Failed to update user status",
      };
    }

    return {
      data: responseData?.data || responseData,
      message: responseData?.message,
      success: responseData?.success,
      error: null,
    };
  } catch (error) {
    console.log(
      "[RBAC Staff] Cognito status error response",
      error?.response?.data || error
    );

    return {
      data: null,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update user status",
    };
  }
};

export const createRbacUser = async (formData) => {
  const url = getRbacUsersUrl();
  const token = getRbacAuthToken();

  if (!url) {
    return {
      data: null,
      error: "RBAC users API configuration is missing.",
    };
  }

  if (!token) {
    return {
      data: null,
      error: "Authorization token is missing. Unable to create user.",
    };
  }

  try {
    console.log("[RBAC Staff] Create user request", { url });
    logRbacFormDataPayload("Create user payload", formData);

    const response = await axios.post(url, formData, {
      headers: getRbacRequestHeaders(),
    });

    console.log("[RBAC Staff] Create user response", response?.data);

    return {
      data: response?.data?.data || response?.data,
      error: null,
    };
  } catch (error) {
    console.log("[RBAC Staff] Create user error response", error?.response?.data || error);

    return {
      data: null,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to onboard staff",
    };
  }
};
